// A table at a pairs event: four people (any seat the bot's if someone has dropped out), the round clock,
// the board result with "Next board", and the round-over state. Builds on the weekly live table's pieces
// (src/components/PartnerHub/LiveTable.js). Your own seat is always at the bottom. Neutral labels only.
import React, { useEffect, useRef, useState } from "react";
import BiddingBox from "../PlayTable/BiddingBox";
import { Avatar, CallText, ContractText, SEAT_NAME, signed } from "../PartnerHub/phUi";
import { Fan, SuitRows, Backs, Auction, Trick, ClaimBox, DealCross, positions, vulSeats } from "../PartnerHub/LiveTable";
import { fmtClock } from "./eventFeed";

const LAST_TRICK_MS = 1500;

function useNow(ms = 1000) {
  const [, setN] = useState(0);
  useEffect(() => { const t = setInterval(() => setN((x) => x + 1), ms); return () => clearInterval(t); }, [ms]);
  return Date.now();
}

function Plate({ seat, t, board, me }) {
  const isBot = t.seats[seat] === "bot";
  const robot = t.robot[seat];
  const name = isBot ? "Robot" : t.names[seat];
  const turn = board && board.toAct === seat && board.phase !== "done";
  const thinking = t.thinking === seat;
  const vul = board && vulSeats(board.vul).includes(seat);
  const partner = seat === positions(me).top;
  let who = seat === me ? "you" : partner ? "partner" : "opponent";
  let sub = `${SEAT_NAME[seat]} · ${who}`;
  if (isBot) sub = `${SEAT_NAME[seat]} · robot`;
  else if (robot) sub = `${SEAT_NAME[seat]} · robot playing for ${who === "you" ? "you" : name}`;
  else if (!t.here[seat] && seat !== me) sub += " · away";
  if (board && board.phase === "play" && seat === board.dummy) sub += " · dummy";
  return (
    <div className={`pth-plate ${turn ? "is-turn" : ""} ${vul ? "is-vul" : ""} ${robot ? "is-robot" : ""}`}>
      {isBot || robot
        ? <span className="pth-bot" aria-hidden="true">{thinking ? <span className="pth-dots3"><i /><i /><i /></span> : "🤖"}</span>
        : <Avatar name={name} online={t.here[seat]} size="sm" />}
      <span className="pth-plateTxt">
        <b>{name}</b>
        <small>{sub}</small>
      </span>
      {board && board.dealer === seat && <span className="pth-dealer" title="Dealer">D</span>}
    </div>
  );
}

function ResultCard({ board, me, t, onNext, sending, rounds, offset = 0 }) {
  const r = board.result;
  const need = board.contract ? board.contract.level + 6 : 0;
  const over = r && board.contract ? r.declarerTricks - need : 0;
  const now = useNow(500) + offset;
  const last = t.boardIdx >= t.boards.length - 1;
  const wait = Math.max(0, (t.nextAt || 0) - now);
  const mine = me === "N" || me === "S" ? r.scoreNS : -r.scoreNS;
  return (
    <section className="pth-card pth-result" aria-label={`Board ${board.boardNo} result`}>
      <div className="pth-resultTop">
        <div>
          <div className="pth-label">Board {board.boardNo}</div>
          <div className="pth-resultContract">
            <ContractText contract={board.contract} declarer={board.declarer} />
            {board.contract && <span className="pth-over">{over === 0 ? "=" : signed(over)}</span>}
          </div>
          {board.claimed !== null && board.claimed !== undefined && <div className="pth-muted pth-small">Claimed</div>}
        </div>
        <div className={`pth-score ${mine > 0 ? "is-plus" : mine < 0 ? "is-minus" : ""}`}>
          <div className="pth-label">{me === "N" || me === "S" ? "North-South" : "East-West"}</div>
          {signed(mine)}
        </div>
      </div>
      <div className="pth-resultBody">
        <DealCross board={board} me={me} />
        <div className="pth-resultAuction"><Auction board={board} /></div>
      </div>
      {!last && t.status === "playing" && (
        <div className="pth-center">
          <button type="button" className="pth-btn pth-btn--primary" disabled={sending} onClick={onNext}>Next board</button>
          <p className="pth-muted pth-small pth-mt">{wait > 0 ? `Next board in ${Math.ceil(wait / 1000)}s, or when everyone presses Next` : "Starting the next board…"}</p>
        </div>
      )}
      {(last || t.status === "finished") && (
        <p className="pth-center pth-muted">{t.round >= rounds ? "Last round finished at this table. Final results follow when every table is done." : `Round ${t.round} finished at this table. The next round starts when every table is done.`}</p>
      )}
    </section>
  );
}

export default function EventTable({ view: t, event, act, sending, offset = 0 }) {
  const me = t.seat;
  const board = t.board;
  const pos = positions(me);
  const now = useNow(1000) + offset;
  const [claim, setClaim] = useState(null);
  const [lastShown, setLastShown] = useState(null);
  const seenTrick = useRef(null);
  const trickTimer = useRef(null);

  useEffect(() => {
    if (!board || !board.lastTrick) return undefined;
    const key = `${board.boardNo}:${board.tricksPlayed}`;
    if (seenTrick.current === key) return undefined;
    const first = seenTrick.current === null;
    seenTrick.current = key;
    if (first || board.trick.length) return undefined;
    setLastShown(board.lastTrick);
    clearTimeout(trickTimer.current);
    trickTimer.current = setTimeout(() => setLastShown(null), LAST_TRICK_MS);
    return undefined;
  }, [board]);
  useEffect(() => {
    if (lastShown && board && (board.trick.length || board.phase !== "play")) setLastShown(null);
  }, [board, lastShown]);
  useEffect(() => () => clearTimeout(trickTimer.current), []);

  const play = (card) => act("evPlay", { card });
  const call = (c) => act("evCall", { call: c });
  const left = Math.max(0, (t.roundEndsAt || 0) - now);
  const clockCls = left < 60000 ? "is-low" : left < 3 * 60000 ? "is-mid" : "";

  const bar = (
    <div className="pth-tbar ev-tbar">
      <div className="pth-tbarTitle">
        <b>{event.name}</b>
        <span className="pth-muted pth-small">Round {t.round} of {event.rounds} · Table {t.tableNo} · Board {board ? board.boardNo : "—"} ({t.boardIdx + 1} of {t.boards.length})</span>
      </div>
      <span className="pth-grow" />
      <span className={`ev-clock ${clockCls}`} title="Time left in this round"><i aria-hidden="true">⏱</i> {t.clockOut ? "Time's up" : fmtClock(left)}</span>
    </div>
  );

  if (!board) return <div className="pth-page pth-page--table">{bar}<p className="pth-muted">Waiting for the board…</p></div>;

  if (board.phase === "done") {
    return (
      <div className="pth-page pth-page--table">
        {bar}
        <ResultCard board={board} me={me} t={t} rounds={event.rounds} offset={offset} sending={sending} onNext={() => act("evNext", {})} />
      </div>
    );
  }

  const myMove = board.myMove && !sending ? board.myMove : null;
  const myMoveIsAuction = myMove && board.phase === "auction";
  const showTrick = lastShown ? lastShown.plays : board.trick;
  const winner = lastShown ? lastShown.winner : null;
  const handAt = (seat) => board.hands[seat];
  const canPlayFrom = (seat) => !!myMove && board.phase === "play" && myMove === seat && !lastShown;
  const nameOf = (seat) => (t.seats[seat] === "bot" || t.robot[seat] ? `the robot (${SEAT_NAME[seat]})` : t.names[seat]);
  const sideHand = (side) => {
    const seat = pos[side];
    const cards = handAt(seat);
    return (
      <div className={`pth-side pth-side--${side}`}>
        <Plate seat={seat} t={t} board={board} me={me} />
        {cards && <SuitRows cards={cards} legal={board.legal} canPlay={canPlayFrom(seat)} onPlay={play} />}
      </div>
    );
  };
  let banner;
  if (board.phase === "auction") banner = myMoveIsAuction ? "Your call" : `${nameOf(board.toAct)} to call`;
  else if (myMove) banner = myMove === me ? "Your card" : `Play from dummy (${SEAT_NAME[myMove]})`;
  else {
    const ctrl = board.toAct === board.dummy && board.declarer ? board.declarer : board.toAct;
    banner = `${nameOf(ctrl)} to play${board.toAct === board.dummy ? " from dummy" : ""}`;
  }
  if (t.clockOut) banner = "Time's up: the robots are finishing this board";

  return (
    <div className="pth-page pth-page--table">
      {bar}
      <div className="pth-felt">
        <div className="pth-felt-top">
          <Plate seat={pos.top} t={t} board={board} me={me} />
          {handAt(pos.top)
            ? <Fan cards={handAt(pos.top)} legal={board.legal} canPlay={canPlayFrom(pos.top)} onPlay={play} small />
            : <Backs n={board.counts[pos.top]} />}
        </div>
        {sideHand("left")}
        <div className="pth-felt-mid">
          {board.phase === "auction" ? <Auction board={board} /> : (
            <>
              <div className="pth-contractBar">
                <ContractText contract={board.contract} declarer={board.declarer} />
                <span className="pth-tricks">N-S {board.ns} · E-W {board.ew}</span>
              </div>
              <Trick plays={showTrick} pos={pos} winner={winner} />
            </>
          )}
        </div>
        {sideHand("right")}
        <div className="pth-felt-bottom">
          <Plate seat={me} t={t} board={board} me={me} />
        </div>
      </div>

      <div className={`pth-turn ${myMove ? "is-me" : ""}`} role="status">{banner}</div>
      {board.auction.length > 0 && board.phase === "play" && (
        <div className="ev-lastCall pth-muted pth-small">Last call: <CallText call={board.auction[board.auction.length - 1].call} /></div>
      )}

      {myMoveIsAuction && !t.clockOut && (
        <div className="pth-bidWrap">
          <BiddingBox auction={board.auction} seat={me} onCall={call} disabled={sending} />
        </div>
      )}
      {handAt(me) && (
        <div className="pth-myHand">
          <Fan cards={handAt(me)} legal={board.legal} canPlay={canPlayFrom(me)} onPlay={play} />
        </div>
      )}
      {board.canClaim && !lastShown && !t.clockOut && (
        <div className="pth-center"><button type="button" className="pth-btn pth-btn--quiet pth-btn--sm" onClick={() => setClaim({ busy: false, msg: "" })}>Claim</button></div>
      )}
      {claim && (
        <ClaimBox
          left={13 - board.ns - board.ew}
          busy={claim.busy}
          msg={claim.msg}
          onCancel={() => setClaim(null)}
          onClaim={async (n) => {
            setClaim({ busy: true, msg: "" });
            const r = await act("evClaim", { tricks: n });
            if (r && r.accepted) setClaim(null);
            else setClaim({ busy: false, msg: r ? "The claim doesn't hold. Play on, or claim fewer." : "" });
          }}
        />
      )}
    </div>
  );
}
